import React, { useState, ChangeEvent } from "react";
import { Box, Button, TextField } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import SystemAlert from "./SystemAlert.tsx";

// Types and Interfaces
interface UploadProps {
  MAX_FILESIZE: number;
  setSnackbarMessage?: (message: string) => void;
  setOpenErrorSnackbar?: (open: boolean) => void;
  setFilePath?: (path: string) => void;
  onFileSelect?: (fileName: string, base64: string | ArrayBuffer | null, objectUrl: string) => void;
  fileName?: string;
  setFileName?: (name: string) => void;
  objectUrl?: string;
}

interface CustomTheme {
  palette: any;
  typography: any;
}

const Upload: React.FC<UploadProps> = ({
  MAX_FILESIZE,
  setSnackbarMessage,
  setOpenErrorSnackbar,
  setFilePath,
  onFileSelect,
  fileName = "",
  setFileName,
  objectUrl,
}) => {
  const theme = useTheme() as CustomTheme;
  const styles = useStyles(theme);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning" | "info">("error");
  const [alertMessage, setAlertMessage] = useState<string>("");

  const allowedTypes: string[] = [
    "image/png",
    "image/jpeg", // includes jpg and jpeg
    "application/pdf",
    "application/vnd.ms-excel", // .xls
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  ];

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      if (!allowedTypes.includes(file.type)) {
        const msg =
          "Unsupported file type. Please upload PNG, JPG, PDF, or Excel files.";
        if (setSnackbarMessage) setSnackbarMessage(msg);
        setAlertMessage(msg);
        setAlertType("error");
        setAlertOpen(true);
        if (setOpenErrorSnackbar) setOpenErrorSnackbar(true);
        return;
      }

      if (file.size > MAX_FILESIZE) {
        const msg = `File size exceeds the ${MAX_FILESIZE / 1048576}MB limit.`;
        if (setSnackbarMessage) setSnackbarMessage(msg);
        setAlertMessage(msg);
        setAlertType("error");
        setAlertOpen(true);
        if (setOpenErrorSnackbar) setOpenErrorSnackbar(true);
        return;
      }

      if (setFileName) setFileName(file.name);
      
      const newObjectUrl = URL.createObjectURL(file);
      if (setFilePath) setFilePath(newObjectUrl);
      
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        const base64 = event.target?.result;
        if (onFileSelect) onFileSelect(file.name, base64 || null, newObjectUrl);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSnackbarClose = (): void => {
    setAlertOpen(false);
  };
  
  return (
    <>
      <Box sx={styles.box_ipt_file}>
        <Button
          component="label"
          variant="contained"
          startIcon={<AttachFileIcon />}
          sx={{ ...styles.btn_primary }}
        >
          Attach *
          <input
            type="file"
            required
            hidden
            accept=".png,.jpg,.jpeg,.pdf,.xls,.xlsx"
            onChange={handleFileChange}
            name="fileattachment"
          />
        </Button>
        <TextField
          variant="outlined"
          disabled
          fullWidth
          placeholder="File name"
          required
          value={fileName}
          size="small"
          sx={styles.ipt_file_attach}
        />
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default Upload;